// member-portal\src\components\SuperAdminPortal.js — FULL FILE
// UPDATED: Added "Promoter Payouts" tab for SuperAdmin
// - New button/tab only visible to SuperAdmin
// - Renders SuperAdminPromoterPayouts component when selected
// - All existing functionality (organizations, deals, billing, analytics) preserved exactly

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './SuperAdminPortal.css';
import NavButtons from './NavButtons';
import AnalyticsChart from './AnalyticsChart';
import SuperAdminBillingPortal from './SuperAdminBillingPortal';
import SuperAdminPromoterPayouts from './SuperAdminPromoterPayouts'; // NEW import

const SuperAdminPortal = ({ user, navigateTo, goBack }) => {
  const [activeTab, setActiveTab] = useState('organizations');
  const [organizations, setOrganizations] = useState([]);
  const [deals, setDeals] = useState([]);
  const [billingData, setBillingData] = useState([]);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({ impressions: [], suspiciousActivities: [] });
  const [dateRange, setDateRange] = useState('30d');
  const [dealIdFilter, setDealIdFilter] = useState('');
  const [orgIdFilter, setOrgIdFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeals, setShowDeals] = useState(false);
  const [showActive, setShowActive] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const MAX_PER_ACTION_FEE = 1500;

  const fetchWithTimeout = async (url, options, timeout = 10000) => {
    const controller = new AbortController();
    const id = setTimeout(() => {
      console.log(`[${new Date().toISOString()}] Request to ${url} timed out after ${timeout}ms`);
      controller.abort();
    }, timeout);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(id);
      return response;
    } catch (err) {
      clearTimeout(id);
      console.error(`[${new Date().toISOString()}] Fetch error for ${url}:`, {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      throw new Error(err.name === 'AbortError' ? `Request to ${url} timed out after ${timeout}ms` : err.message);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      if (!token) throw new Error('No authentication token found');
      console.log(`[${new Date().toISOString()}] Fetching data for SuperAdminPortal, showDeals: ${showDeals}`);
      if (!showDeals) {
        const orgResponse = await fetchWithTimeout(`${process.env.REACT_APP_API_URL}/api/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }, 10000);
        if (!orgResponse.ok) {
          const errorData = await orgResponse.json();
          throw new Error(errorData.error || `Failed to fetch organizations (status: ${orgResponse.status})`);
        }
        const orgData = await orgResponse.json();
        console.log(`[${new Date().toISOString()}] Fetched ${orgData.length} organizations`);
        setOrganizations(orgData);
      } else {
        const dealResponse = await fetchWithTimeout(`${process.env.REACT_APP_API_URL}/api/members/all-deals`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }, 10000);
        if (!dealResponse.ok) {
          const errorData = await dealResponse.json();
          throw new Error(errorData.error || `Failed to fetch deals (status: ${dealResponse.status})`);
        }
        const dealData = await dealResponse.json();
        console.log(`[${new Date().toISOString()}] Fetched ${dealData.length} deals`);
        setDeals(dealData);
      }
      setError(null);
    } catch (err) {
      console.error(`[${new Date().toISOString()}] Error fetching data:`, {
        message: err.message,
        name: err.name,
        stack: err.stack
      });
      setError(err.message);
      toast.error(`Failed to fetch data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [showDeals]);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/superadmin/billing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingData(response.data);
      setError(null);
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error fetching billing data:`, error);
      setError('Failed to load billing data');
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceHistory = async (orgId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/superadmin/invoices/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      if (!response.data || !Array.isArray(response.data)) {
        console.warn(`[${new Date().toISOString()}] Invalid invoice history response for orgId ${orgId}:`, response.data);
        setError('No invoices found for this organization.');
        setInvoiceHistory([]);
        return;
      }
      console.log(`[${new Date().toISOString()}] Fetched ${response.data.length} invoices for orgId ${orgId}`);
      setInvoiceHistory(response.data);
      setError(null);
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error fetching invoice history:`, {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status
      });
      if (error.response?.status === 404) {
        setError('No invoices found for this organization.');
        setInvoiceHistory([]);
      } else if (error.code === 'ECONNABORTED') {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        setError(error.response?.data?.error || 'Failed to load invoice history. Please try again.');
        toast.error('Failed to load invoice history');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const params = { dateRange };
      if (dealIdFilter) params.dealId = dealIdFilter;
      if (orgIdFilter) params.orgId = orgIdFilter;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/superadmin/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        timeout: 10000
      });
      setAnalyticsData(response.data);
      setError(null);
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error fetching analytics data:`, error);
      setError('Failed to load analytics data');
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoicePreview = async (orgId, invoiceId = null, retries = 2) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const url = invoiceId
        ? `${process.env.REACT_APP_API_URL}/api/superadmin/billing/invoice/${invoiceId}/review`
        : `${process.env.REACT_APP_API_URL}/api/superadmin/invoice/preview/${orgId}`;
      console.log(`[${new Date().toISOString()}] Fetching invoice preview: ${url}, attempt ${3 - retries}`);
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      console.log(`[${new Date().toISOString()}] Invoice preview response:`, {
        previewUrl: response.data.previewUrl,
        uploadUrl: response.data.uploadUrl,
        hasPdfBase64: !!response.data.pdfBase64
      });
      return response.data;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error fetching invoice preview:`, error.message, error.code);
      if (retries > 0 && (error.code === 'ECONNABORTED' || error.response?.status === 503)) {
        console.log(`[${new Date().toISOString()}] Retrying invoice preview fetch, ${retries} attempts left`);
        return fetchInvoicePreview(orgId, invoiceId, retries - 1);
      }
      setError('Failed to generate invoice preview. Please try again or contact support.');
      toast.error('Failed to generate invoice preview');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const uploadToS3 = async (uploadUrl, pdfBase64) => {
    try {
      console.log(`[${new Date().toISOString()}] Uploading PDF to S3:`, { uploadUrl });
      const binaryString = atob(pdfBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const pdfBlob = new Blob([bytes], { type: 'application/pdf' });

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: pdfBlob,
        headers: {
          'Content-Type': 'application/pdf'
        },
      });
      if (!response.ok) {
        let errorDetails = '';
        try {
          errorDetails = await response.text();
        } catch (jsonErr) {
          errorDetails = 'No additional error details available';
        }
        console.error(`[${new Date().toISOString()}] S3 upload failed:`, {
          status: response.status,
          statusText: response.statusText,
          details: errorDetails
        });
        throw new Error(`Failed to upload PDF to S3: ${response.statusText}. Details: ${errorDetails}`);
      }
      console.log(`[${new Date().toISOString()}] Successfully uploaded PDF to S3`);
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error uploading PDF to S3:`, error);
      toast.error(`Failed to upload invoice to S3: ${error.message}`);
      throw error;
    }
  };

  const previewInvoice = async (orgId, invoiceId = null) => {
    try {
      console.log(`[${new Date().toISOString()}] Starting previewInvoice:`, { orgId, invoiceId });
      const invoiceData = await fetchInvoicePreview(orgId, invoiceId);

      const editorUrl = invoiceId
        ? `/invoice-editor?orgId=${orgId}&invoiceId=${invoiceId}&previewUrl=${encodeURIComponent(invoiceData.previewUrl)}`
        : `/invoice-editor?orgId=${orgId}&previewUrl=${encodeURIComponent(invoiceData.previewUrl)}`;
      const newWindow = window.open(editorUrl, '_blank', 'width=900,height=600');

      if (!newWindow) {
        console.error(`[${new Date().toISOString()}] Failed to open invoice editor window. Pop-up blocked?`);
        setError('Please allow pop-ups to view the invoice editor.');
        toast.error('Please allow pop-ups to view the invoice editor');
        return;
      }

      newWindow.focus();

      let attempts = 0;
      const maxAttempts = 20;
      const sendData = () => {
        if (newWindow.closed || attempts >= maxAttempts) {
          clearInterval(interval);
          if (attempts >= maxAttempts) {
            console.error(`[${new Date().toISOString()}] Failed to send INVOICE_DATA after max attempts`);
            setError('Invoice editor may not have loaded correctly. Please try again.');
            toast.error('Invoice editor may not have loaded correctly');
          }
          return;
        }
        try {
          if (newWindow.document.readyState === 'complete') {
            newWindow.postMessage({ type: 'INVOICE_DATA', data: invoiceData }, window.location.origin);
            console.log(`[${new Date().toISOString()}] Sent INVOICE_DATA to editor window (attempt ${attempts + 1})`);
          } else {
            console.log(`[${new Date().toISOString()}] Editor window not fully loaded (readyState: ${newWindow.document.readyState})`);
          }
          attempts++;
        } catch (e) {
          console.log(`[${new Date().toISOString()}] Editor window not ready yet:`, e);
          attempts++;
        }
      };

      const interval = setInterval(sendData, 500);
      setTimeout(() => clearInterval(interval), 10000);

      const handleAck = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data.type === 'INVOICE_DATA_ACK') {
          console.log(`[${new Date().toISOString()}] Received INVOICE_DATA_ACK from editor`);
          clearInterval(interval);
        }
      };
      window.addEventListener('message', handleAck);

      const cleanup = () => {
        window.removeEventListener('message', handleAck);
        clearInterval(interval);
      };
      newWindow.addEventListener('unload', cleanup);

      if (invoiceData.uploadUrl && invoiceData.pdfBase64) {
        console.log(`[${new Date().toISOString()}] Uploading new invoice to S3`);
        try {
          await uploadToS3(invoiceData.uploadUrl, invoiceData.pdfBase64);
        } catch (uploadError) {
          console.error(`[${new Date().toISOString()}] S3 upload failed, but continuing with preview:`, uploadError);
          setError(`Failed to upload invoice to S3: ${uploadError.message}. You can still view the preview.`);
          toast.error(`Failed to upload invoice to S3: ${uploadError.message}`);
        }
      } else {
        console.log(`[${new Date().toISOString()}] No S3 upload required (existing invoice)`);
      }
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Preview invoice error:`, error);
      setError(`Failed to generate invoice preview: ${error.message}. Please try again or contact support.`);
      toast.error(`Failed to generate invoice preview: ${error.message}`);
    }
  };

  const emailInvoice = async (orgId, invoiceId = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const url = invoiceId
        ? `${process.env.REACT_APP_API_URL}/api/superadmin/invoice/email/${orgId}/${invoiceId}`
        : `${process.env.REACT_APP_API_URL}/api/superadmin/invoice/email/${orgId}`;
      console.log(`[${new Date().toISOString()}] Sending email for invoice: ${url}`);
      const response = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      if (response.data.uploadUrl && response.data.pdfBase64) {
        console.log(`[${new Date().toISOString()}] Uploading new invoice to S3 for email`);
        await uploadToS3(response.data.uploadUrl, response.data.pdfBase64);
      } else {
        console.log(`[${new Date().toISOString()}] No S3 upload required for email (existing invoice)`);
      }

      toast.success('Invoice emailed successfully');
      setError(null);
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error emailing invoice:`, error);
      setError('Failed to email invoice. Please try again.');
      toast.error('Failed to email invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (orgId, status) => {
    try {
      const token = localStorage.getItem('memberToken');
      console.log(`[${new Date().toISOString()}] Verifying organization ${orgId} with status ${status}`);
      const response = await fetchWithTimeout(`${process.env.REACT_APP_API_URL}/api/organizations/${orgId}/verify`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ verificationStatus: status }),
      }, 10000);
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonErr) {
          errorData = { error: `HTTP ${response.status} ${response.statusText}` };
        }
        console.error(`[${new Date().toISOString()}] Verification failed:`, {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(errorData.error || `Failed to update verification status (status: ${response.status})`);
      }
      const updatedOrg = await response.json();
      setOrganizations(organizations.map(org => org._id === orgId ? updatedOrg : org));
      console.log(`[${new Date().toISOString()}] Updated verification status for organization ${orgId} to ${status}`);
      toast.success(`Organization verification status updated to ${status}`);
      setError(null);
    } catch (err) {
      console.error(`[${new Date().toISOString()}] Error updating verification:`, err);
      setError(err.message);
      toast.error(`Failed to update verification: ${err.message}`);
    }
  };

  const handleToggleActive = async (orgId) => {
    try {
      const token = localStorage.getItem('memberToken');
      console.log(`[${new Date().toISOString()}] Toggling active status for organization ${orgId}`);
      const response = await fetchWithTimeout(`${process.env.REACT_APP_API_URL}/api/organizations/${orgId}/toggle-active`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }, 10000);
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonErr) {
          errorData = { error: `HTTP ${response.status} ${response.statusText}` };
        }
        console.error(`[${new Date().toISOString()}] Toggle active status failed:`, {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(errorData.error || `Failed to toggle active status (status: ${response.status})`);
      }
      const updatedOrg = await response.json();
      setOrganizations(organizations.map(org => org._id === orgId ? updatedOrg : org));
      console.log(`[${new Date().toISOString()}] Toggled active status for organization ${orgId} to ${updatedOrg.isActive ? 'active' : 'deactivated'}`);
      toast.success(updatedOrg.isActive
        ? 'Organization and its members reactivated. Deals remain deactivated and must be manually reactivated.'
        : 'Organization, its members, and deals deactivated.');
      setError(null);
    } catch (err) {
      console.error(`[${new Date().toISOString()}] Error toggling active status:`, err);
      setError(err.message);
      toast.error(`Failed to toggle active status: ${err.message}`);
    }
  };

  const handleToggleDealStatus = async (dealId) => {
    try {
      const token = localStorage.getItem('memberToken');
      console.log(`[${new Date().toISOString()}] Toggling deal status for deal ${dealId}`);
      const response = await fetchWithTimeout(`${process.env.REACT_APP_API_URL}/api/deals/${dealId}/deactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }, 10000);
      if (!response.ok) {
        const errorData = await response.json();
        console.error(`[${new Date().toISOString()}] Toggle deal status failed:`, errorData);
        throw new Error(errorData.error || `Failed to toggle deal status (status: ${response.status})`);
      }
      const updatedDeal = await response.json();
      setDeals(deals.map(deal => deal._id === dealId ? { ...deal, active: updatedDeal.deal.active } : deal));
      console.log(`[${new Date().toISOString()}] Toggled deal status for deal ${dealId} to ${updatedDeal.deal.active ? 'active' : 'inactive'}`);
      toast.success(`Deal status updated to ${updatedDeal.deal.active ? 'active' : 'inactive'}`);
    } catch (err) {
      console.error(`[${new Date().toISOString()}] Error toggling deal status:`, err);
      setError(err.message);
      toast.error(`Failed to toggle deal status: ${err.message}`);
    }
  };

  const handleViewDeal = (deal) => {
    console.log(`[${new Date().toISOString()}] Navigating to dealDetail for deal ${deal._id}`);
    navigateTo('dealDetail', { deal });
  };

  const handleEditDeal = (deal) => {
    console.log(`[${new Date().toISOString()}] Navigating to dealForm for editing deal ${deal._id}`);
    navigateTo('dealForm', { deal, isEdit: true });
  };

  // ─────────────────────────────────────────────────────────────
  // FIXED: SuperAdmin "View" now opens editable OrganizationForm
  // Passes full org data + user.role so form knows it's SuperAdmin
  // ─────────────────────────────────────────────────────────────
  const handleViewOrg = (org) => {
    console.log(`[${new Date().toISOString()}] Navigating to organization view (SuperAdmin edit) for org ${org._id}`);
    navigateTo('organization', { org, user });  // Pass full user object so OrganizationForm can check role
  };
  // ─────────────────────────────────────────────────────────────

  const normalizeText = (text) => {
    return text
      ? text
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
          .toLowerCase()
      : '';
  };

  const filteredOrganizations = organizations.filter(org => {
    const isActiveOrPending = showActive ? (org.isActive || org.verificationStatus === 'pending') : !org.isActive;
    if (!searchQuery) return isActiveOrPending;
    const query = normalizeText(searchQuery);
    return isActiveOrPending && (
      normalizeText(org.companyName).includes(query) ||
      normalizeText(org.enterpriseName).includes(query) ||
      normalizeText(org.registrationNumber).includes(query)
    );
  });

  const filteredDeals = deals.filter(deal => {
    const isActive = showActive ? deal.active : !deal.active;
    if (!searchQuery) return isActive;
    const query = normalizeText(searchQuery);
    return isActive && (
      normalizeText(deal._id).includes(query) ||
      normalizeText(deal.organizationId?.companyName || '').includes(query) ||
      normalizeText(deal.title).includes(query) ||
      normalizeText(deal.description).includes(query)
    );
  });

  const filteredBillingData = billingData.filter(org => {
    if (!searchQuery) return true;
    const query = normalizeText(searchQuery);
    return (
      normalizeText(org.companyName).includes(query) ||
      normalizeText(org.accountNumber).includes(query)
    );
  });

  const filteredAnalyticsData = {
    impressions: analyticsData.impressions.filter(imp => {
      if (!searchQuery) return true;
      const query = normalizeText(searchQuery);
      return (
        normalizeText(imp.dealTitle).includes(query) ||
        normalizeText(imp.orgName).includes(query) ||
        normalizeText(imp.country || '').includes(query)
      );
    }),
    suspiciousActivities: analyticsData.suspiciousActivities.filter(activity => {
      if (!searchQuery) return true;
      const query = normalizeText(searchQuery);
      return (
        normalizeText(activity.dealId?.title || '').includes(query) ||
        normalizeText(activity.ipAddress).includes(query) ||
        normalizeText(activity.geoLocation?.country || '').includes(query)
      );
    })
  };

  useEffect(() => {
    if (activeTab === 'organizations' || activeTab === 'deals') {
      fetchData();
    } else if (activeTab === 'analytics') {
      fetchAnalyticsData();
    } else if (activeTab === 'billing') {
      fetchBillingData();
    }
  }, [activeTab, showDeals, fetchData]);

  const renderOrganizationsView = () => (
    <div>
      <h2>Organization Verification</h2>
      <div className="super-admin-actions">
        <button onClick={() => setShowDeals(!showDeals)} className="toggle-view">
          {showDeals ? 'View Organizations' : 'View Deals'}
        </button>
        <button onClick={() => setShowActive(!showActive)} className="toggle-view">
          {showActive ? 'Inactive' : 'Active'}
        </button>
        <input
          type="text"
          placeholder={showDeals ? "Search deals..." : "Search organizations..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-bar"
        />
      </div>
      {showDeals ? (
        <table>
          <thead>
            <tr>
              <th className="deal-id-col">Deal ID</th>
              <th className="deal-org-col">Organization</th>
              <th className="title-col">Title</th>
              <th className="desc-col">Description</th>
              <th className="cat-col">Category</th>
              <th className="price-col">Price</th>
              <th className="deal-status-col">Status</th>
              <th className="deal-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDeals.map(deal => (
              <tr key={deal._id}>
                <td className="deal-id-col">{deal._id}</td>
                <td className="deal-org-col">{deal.organizationId?.companyName || 'N/A'}</td>
                <td className="title-col">{deal.title}</td>
                <td className="desc-col">{deal.description}</td>
                <td className="cat-col">{deal.category?.map(cat => (typeof cat === 'object' ? cat.name : cat)).join(', ') || 'None'}</td>
                <td className="price-col">{deal.price ? `R${deal.price}` : 'N/A'}</td>
                <td className="deal-status-col">{deal.active ? 'Active' : 'Inactive'}</td>
                <td className="deal-actions-col">
                  <div className="actions-column">
                    <button onClick={() => handleViewDeal(deal)} className="action-button view">View</button>
                    <button onClick={() => handleEditDeal(deal)} className="action-button edit">Edit</button>
                    <button
                      onClick={() => handleToggleDealStatus(deal._id)}
                      className={`action-button ${deal.active ? 'deactivate' : 'activate'}`}
                    >
                      {deal.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="org-col">Company Name</th>
              <th className="enterprise-col">Enterprise Name</th>
              <th className="reg-col">Registration Number</th>
              <th className="email-col">Email Notifications</th>
              <th className="status-col">Verification Status</th>
              <th className="active-col">Active</th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrganizations.map(org => (
              <tr key={org._id}>
                <td className="org-col">{org.companyName}</td>
                <td className="enterprise-col">{org.enterpriseName}</td>
                <td className="reg-col">{org.registrationNumber}</td>
                <td className="email-col">{org.emailNotifications}</td>
                <td className="status-col">{org.verificationStatus}</td>
                <td className="active-col">{org.isActive ? 'Active' : 'Inactive'}</td>
                <td className="actions-col">
                  <div className="actions-column">
                    <button onClick={() => handleViewOrg(org)} className="action-button view">View</button>
                    {org.verificationStatus === 'pending' && (
                      <>
                        <button onClick={() => handleVerify(org._id, 'approved')} className="action-button approve">Approve</button>
                        <button onClick={() => handleVerify(org._id, 'rejected')} className="action-button reject">Reject</button>
                      </>
                    )}
                    <button
                      onClick={() => handleToggleActive(org._id)}
                      className={`action-button ${org.isActive ? 'deactivate' : 'activate'}`}
                    >
                      {org.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderBillingView = () => (
    <SuperAdminBillingPortal navigateTo={navigateTo} />
  );

  const renderAnalyticsView = () => (
    <div>
      <h2>Analytics Dashboard</h2>
      <div className="super-admin-actions">
        <input
          type="text"
          placeholder="Search analytics..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-bar"
        />
      </div>
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <>
          <div className="analytics-filters">
            <label>
              Date Range:
              <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
            </label>
            <label>
              Deal ID:
              <input
                type="text"
                value={dealIdFilter}
                onChange={(e) => setDealIdFilter(e.target.value)}
                placeholder="Enter Deal ID"
              />
            </label>
            <label>
              Organization ID:
              <input
                type="text"
                value={orgIdFilter}
                onChange={(e) => setOrgIdFilter(e.target.value)}
                placeholder="Enter Org ID"
              />
            </label>
          </div>
          <AnalyticsChart impressions={filteredAnalyticsData.impressions} />
          <h3>Impression Metrics</h3>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Deal Title</th>
                <th>Organization</th>
                <th>Type</th>
                <th>Country</th>
                <th>Device Type</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {filteredAnalyticsData.impressions.map((imp, index) => (
                <tr key={index}>
                  <td>{imp.dealTitle}</td>
                  <td>{imp.orgName}</td>
                  <td>{imp.type}</td>
                  <td>{imp.country || 'Unknown'}</td>
                  <td>{imp.deviceType}</td>
                  <td>{imp.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h3>Suspicious Activities</h3>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Deal Title</th>
                <th>Type</th>
                <th>IP Address</th>
                <th>Country</th>
                <th>Details</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {filteredAnalyticsData.suspiciousActivities.map((activity, index) => (
                <tr key={index}>
                  <td>{activity.dealId?.title || 'Unknown'}</td>
                  <td>{activity.type}</td>
                  <td>{activity.ipAddress}</td>
                  <td>{activity.geoLocation?.country || 'Unknown'}</td>
                  <td>{activity.details}</td>
                  <td>{new Date(activity.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  const renderPromoterPayoutsView = () => (
    <SuperAdminPromoterPayouts user={user} />
  );

  if (user.email !== 'admin@scover.co.za' || user.role !== 'SuperAdmin') {
    return (
      <div className="super-admin-portal">
        <NavButtons navigateTo={navigateTo} user={user} activePage="superAdmin" />
        <p className="error">Access denied. This page is only for Super Admins.</p>
      </div>
    );
  }

  return (
    <div className="super-admin-portal">
      <NavButtons navigateTo={navigateTo} user={user} activePage="superAdmin" isSuperAdmin={true} />
      {error && <p className="error">{error}</p>}
      <div className="tabs">
        <button 
          onClick={() => setActiveTab('organizations')} 
          className={activeTab === 'organizations' ? 'active' : ''}
        >
          Organizations
        </button>
        <button 
          onClick={() => setActiveTab('billing')} 
          className={activeTab === 'billing' ? 'active' : ''}
        >
          Billing
        </button>
        <button 
          onClick={() => setActiveTab('analytics')} 
          className={activeTab === 'analytics' ? 'active' : ''}
        >
          Analytics
        </button>
        <button 
          onClick={() => setActiveTab('promoter-payouts')} 
          className={activeTab === 'promoter-payouts' ? 'active' : ''}
        >
          Promoter Payouts
        </button>
      </div>

      {activeTab === 'organizations' ? renderOrganizationsView() :
       activeTab === 'billing' ? renderBillingView() :
       activeTab === 'analytics' ? renderAnalyticsView() :
       activeTab === 'promoter-payouts' ? renderPromoterPayoutsView() : null}
    </div>
  );
};

export default SuperAdminPortal;